<template>
  <modal
    :show="isVisible"
    :title="t('Notificar cliente')"
    size="medium"
    elem-id="modal_pdv_entregas_notify"
    data-testid="pdv-entregas-notify-modal"
    :close-label="t('Cancelar')"
    close-test-id="pdv-entregas-notify-modal-cancel-btn"
    :save-label="t('Enviar')"
    save-icon="pi pi-whatsapp"
    save-test-id="pdv-entregas-notify-modal-send-btn"
    :save-disabled-fn="saveDisabledFn"
    :save-fn="handleSend"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div v-if="isVisible" class="notify-modal-body">
      <!-- Loading state -->
      <div v-if="isLoadingAlerts" class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        {{ t('Carregando itens...') }}
      </div>

      <!-- Item list -->
      <div v-else-if="customerAlerts.length > 0" class="items-section">
        <p class="section-label">{{ t('Selecione os itens a notificar:') }}</p>
        <div
          v-for="row in customerAlerts"
          :key="row.so_order_item_id"
          :data-testid="`pdv-entregas-notify-item-${row.so_order_item_id}`"
          class="item-row"
        >
          <label class="item-label">
            <input
              type="checkbox"
              :data-testid="`pdv-entregas-notify-item-checkbox-${row.so_order_item_id}`"
              :checked="selectedItemIds.has(row.so_order_item_id)"
              @change="toggleItem(row.so_order_item_id)"
            />
            <span class="item-text">
              [{{ row.so_order_item_qrcode ?? '-' }}]
              {{ row.so_service_name ?? t('Item') }}
              (OS #{{ row.so_order_id }})
              — {{ t('pronto há') }} {{ row.days_since_ready ?? 0 }} {{ (row.days_since_ready ?? 0) === 1 ? t('dia') : t('dias') }}
            </span>
          </label>
        </div>
      </div>

      <div v-else class="empty-state">
        {{ t('Nenhum item pendente para este cliente.') }}
      </div>

      <!-- Rich-text message editor -->
      <div class="message-section">
        <p class="section-label">{{ t('Mensagem:') }}</p>
        <input-rich-text
          v-model="messageHtml"
          elem-id="pdv-entregas-notify-message"
          data-testid="pdv-entregas-notify-message-editor"
          :label="t('Mensagem WhatsApp')"
          :show-toolbar="true"
          :min-height="'120px'"
        />
        <button
          type="button"
          class="restore-btn"
          data-testid="pdv-entregas-notify-restore-template-btn"
          @click="restoreTemplate"
        >
          {{ t('Restaurar template') }}
        </button>
      </div>

      <!-- Portal do cliente — Trigger B (plano-gatilhos-notificacao.md §4).
           A separate action from "Enviar" above: this one is the system
           actually sending, not the counter drafting a message, so it has
           its own button and its own per-item outcome, not the shared
           WhatsApp message text. Hidden entirely for a tenant without the
           portal — no button, no request. -->
      <div v-if="portalClienteEnabled" class="portal-section">
        <p class="section-label">{{ t('Portal do cliente:') }}</p>
        <button
          type="button"
          class="portal-send-btn"
          data-testid="pdv-entregas-notify-portal-send-btn"
          :disabled="isSendingPortal || selectedItemIds.size === 0"
          @click="handleSendPortal"
        >
          <i v-if="isSendingPortal" class="pi pi-spin pi-spinner"></i>
          {{ t('Avisar pelo portal') }}
        </button>

        <ul v-if="portalResultRows.length > 0" class="portal-results-list">
          <li
            v-for="row in portalResultRows"
            :key="row.itemId"
            :data-testid="`pdv-entregas-notify-portal-result-${row.itemId}`"
          >
            <span class="portal-result-label">
              [{{ row.qrcode ?? '-' }}] {{ row.serviceName ?? t('Item') }} —
            </span>

            <span
              v-if="row.outcome === 'sent'"
              data-testid="pdv-entregas-notify-portal-status-avisado"
            >
              {{ t('Avisado') }}
            </span>
            <span
              v-else-if="row.outcome === 'no_devices'"
              data-testid="pdv-entregas-notify-portal-status-caixa-entrada"
            >
              {{ t('Na caixa de entrada — o cliente não ativou avisos no celular') }}
            </span>
            <template v-else-if="row.outcome === 'no_portal_account'">
              <span data-testid="pdv-entregas-notify-portal-status-guardado">
                {{ t('Guardado — este cliente ainda não tem acesso ao portal') }}
              </span>
              <button
                type="button"
                class="portal-create-access-btn"
                data-testid="pdv-entregas-notify-portal-criar-acesso-btn"
                @click="abrirCriarAcessoPortal"
              >
                {{ t('Criar acesso') }}
              </button>
            </template>
            <span
              v-else
              data-testid="pdv-entregas-notify-portal-status-falha"
            >
              {{ t('Falha ao avisar pelo portal') }}
            </span>
          </li>
        </ul>
      </div>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';
import { useSOEntregas } from '../../composables/useSOEntregas';
import { config } from '../../services/configService';
import type { Orders_EntregasItemRow } from '../../contracts/Orders_EntregasItemRow';

const { t } = useI18n();
const { fetchPendingPickupAlerts, markPickupNotified, sendPortalPickupNotification } = useSOEntregas();

// A tenant without a customers portal (Doutor Sofá, OMB) lists `portalcliente`
// in AWRENV_SISTEMAAPI_DISABLED_FEATURES — no button renders and no request is ever issued.
const portalClienteEnabled = config.isFeatureEnabled('portalcliente');

// ─── State ─────────────────────────────────────────────────────────────────

const isVisible = ref(false);
const isLoadingAlerts = ref(false);
const isSending = ref(false);
const customerId = ref<number | null>(null);
const triggerItemId = ref<number | null>(null);
const customerAlerts = ref<Orders_EntregasItemRow[]>([]);
const selectedItemIds = ref(new Set<number>());
const messageHtml = ref('');

// ─── Portal channel (Trigger B) ────────────────────────────────────────────

type PortalOutcome = 'sent' | 'no_devices' | 'no_portal_account' | 'error';

const isSendingPortal = ref(false);
/** Keyed by so_order_item_id — one outcome per item, never one aggregate toast. */
const portalResults = ref<Record<number, PortalOutcome>>({});

const portalResultRows = computed(() =>
  customerAlerts.value
    .filter((row) => row.so_order_item_id in portalResults.value)
    .map((row) => ({
      itemId: row.so_order_item_id,
      qrcode: row.so_order_item_qrcode,
      serviceName: row.so_service_name,
      outcome: portalResults.value[row.so_order_item_id],
    })),
);

// ─── Helpers ───────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildDefaultTemplate(
  customerName: string,
  selectedRows: Orders_EntregasItemRow[],
): string {
  const bullets = selectedRows
    .map(
      (r) =>
        `<li>${escapeHtml(r.so_service_name ?? t('Item'))} (OS #${r.so_order_id}, QR ${escapeHtml(r.so_order_item_qrcode ?? '-')})</li>`,
    )
    .join('');
  return [
    `<p>${t('Olá')}, ${escapeHtml(customerName)}!</p>`,
    `<p></p>`,
    `<p>${t('Informamos que os seguintes itens estão prontos para retirada:')}</p>`,
    `<ul>${bullets}</ul>`,
    `<p>${t('Aguardamos sua visita!')}</p>`,
  ].join('');
}

function toggleItem(id: number): void {
  const next = new Set(selectedItemIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  selectedItemIds.value = next;
}

function restoreTemplate(): void {
  const selectedRows = customerAlerts.value.filter((r) =>
    selectedItemIds.value.has(r.so_order_item_id),
  );
  const customerName = customerAlerts.value[0]?.so_order_client_name ?? '';
  messageHtml.value = buildDefaultTemplate(customerName, selectedRows);
}

// ─── HTML → WhatsApp text ──────────────────────────────────────────────────

function walk(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof Element)) return '';
  const inner = Array.from(node.childNodes).map(walk).join('');
  const tag = node.tagName.toLowerCase();
  switch (tag) {
    case 'strong':
    case 'b':
      return `*${inner}*`;
    case 'em':
    case 'i':
      return `_${inner}_`;
    case 's':
    case 'strike':
      return `~${inner}~`;
    case 'li':
      return `• ${inner}\n`;
    case 'p':
      return `${inner}\n`;
    case 'br':
      return '\n';
    default:
      return inner;
  }
}

function htmlToWhatsappText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<root>${html}</root>`, 'text/html');
  return walk(doc.querySelector('root') as Element).trim();
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<root>${html}</root>`, 'text/html');
  return (doc.querySelector('root')?.textContent ?? '').trim();
}

// ─── Save disabled ─────────────────────────────────────────────────────────

function saveDisabledFn(): boolean {
  return (
    isSending.value ||
    selectedItemIds.value.size === 0 ||
    stripHtml(messageHtml.value).trim().length === 0
  );
}

// ─── handleSend ────────────────────────────────────────────────────────────

async function handleSend(): Promise<void> {
  if (saveDisabledFn()) return;
  const ids = Array.from(selectedItemIds.value);
  const phone = (customerAlerts.value[0]?.cliente_whatsapp_phone ?? '').replace(/\D/g, '');
  const waMessage = htmlToWhatsappText(messageHtml.value);

  if (phone) {
    window.open(
      // api.whatsapp.com/send direto — o redirect do wa.me corrompe os emojis
      // (UTF-8 → �) ao repassar o ?text. Enviando direto, o encodeURIComponent
      // preserva os emojis corretamente.
      `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(waMessage)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  isSending.value = true;
  try {
    const auditNote = waMessage.slice(0, 240);
    await Promise.all(ids.map((id) => markPickupNotified(id, 'whatsapp', auditNote)));
    const successText = ids.length === 1
      ? `1 ${t('item notificado')}`
      : `${ids.length} ${t('itens notificados')}`;
    eventBus.emit('globalMessage.success', successText);
    eventBus.emit('pdv-entregas.notifications.sent');
    handleClose();
  } catch (err) {
    logError('PDVEntregasNotifyModal', 'handleSend', 'bulk notify failed', err);
    const msg = err instanceof Error ? err.message : (err as any)?.message;
    eventBus.emit('globalMessage.error', msg || t('Erro ao notificar em lote'));
  } finally {
    isSending.value = false;
  }
}

// ─── handleSendPortal (Trigger B) ──────────────────────────────────────────

/**
 * Sends the "ready for pickup" portal notification for every selected item.
 *
 * Promise.allSettled — NOT Promise.all like handleSend() above — because a
 * portal failure on one item must be reported for THAT item, never swallow
 * the others into one aggregate failure toast (the whole point of the three
 * distinct outcomes is per-item visibility).
 */
async function handleSendPortal(): Promise<void> {
  if (isSendingPortal.value || selectedItemIds.value.size === 0) return;

  const ids = Array.from(selectedItemIds.value);
  isSendingPortal.value = true;
  try {
    const settled = await Promise.allSettled(ids.map((id) => sendPortalPickupNotification(id)));

    const nextResults: Record<number, PortalOutcome> = { ...portalResults.value };
    let sentCount = 0;
    let failedCount = 0;

    settled.forEach((result, index) => {
      const id = ids[index];
      if (result.status === 'fulfilled') {
        const { warning } = result.value;
        nextResults[id] = warning === 'no_portal_account' || warning === 'no_devices' ? warning : 'sent';
        sentCount += 1;
      } else {
        nextResults[id] = 'error';
        failedCount += 1;
        logError('PDVEntregasNotifyModal', 'handleSendPortal', 'portal notify failed for item', { id, err: result.reason });
      }
    });

    portalResults.value = nextResults;

    if (failedCount === 0) {
      eventBus.emit(
        'globalMessage.success',
        sentCount === 1 ? `1 ${t('item avisado pelo portal')}` : `${sentCount} ${t('itens avisados pelo portal')}`,
      );
    } else if (sentCount === 0) {
      eventBus.emit('globalMessage.error', t('Falha ao avisar pelo portal'));
    } else {
      eventBus.emit(
        'globalMessage.warning',
        `${sentCount} ${t('avisados')}, ${failedCount} ${t('com falha')}`,
      );
    }

    eventBus.emit('pdv-entregas.notifications.sent');
  } finally {
    isSendingPortal.value = false;
  }
}

/**
 * The one outcome that matters most: the customer is right there, so this is
 * the best possible moment to create their portal access. Opens the existing
 * ClienteModal on its portal tab rather than duplicating that flow here.
 */
function abrirCriarAcessoPortal(): void {
  if (customerId.value === null) return;
  eventBus.emit('modal.cliente.open', { id: customerId.value, tab: 'portal' });
}

// ─── Open / Close ──────────────────────────────────────────────────────────

async function handleOpen(payload: { itemId: number; customerId?: number }): Promise<void> {
  triggerItemId.value = payload.itemId;
  isVisible.value = true;

  isLoadingAlerts.value = true;
  try {
    const all = await fetchPendingPickupAlerts();
    const resolvedCustomerId =
      payload.customerId ??
      all.find((r) => r.so_order_item_id === payload.itemId)?.so_order_client_id ??
      null;
    if (resolvedCustomerId === null) {
      eventBus.emit('globalMessage.warning', t('Item não disponível para notificação'));
      handleClose();
      return;
    }
    customerId.value = resolvedCustomerId;
    customerAlerts.value = all.filter((r) => r.so_order_client_id === resolvedCustomerId);
    selectedItemIds.value = new Set([payload.itemId]);
  } catch (err) {
    logError('PDVEntregasNotifyModal', 'open', 'failed to fetch alerts', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar itens do cliente'));
  } finally {
    isLoadingAlerts.value = false;
  }
}

function handleClose(): void {
  isVisible.value = false;
  customerId.value = null;
  triggerItemId.value = null;
  customerAlerts.value = [];
  selectedItemIds.value = new Set();
  messageHtml.value = '';
  isSendingPortal.value = false;
  portalResults.value = {};
}

// ─── Watcher: recompute template on selection or alerts change ─────────────

watch(
  [selectedItemIds, customerAlerts],
  () => {
    const selectedRows = customerAlerts.value.filter((r) =>
      selectedItemIds.value.has(r.so_order_item_id),
    );
    const customerName = customerAlerts.value[0]?.so_order_client_name ?? '';
    messageHtml.value = buildDefaultTemplate(customerName, selectedRows);
  },
  { deep: true },
);

// ─── Lifecycle ─────────────────────────────────────────────────────────────

const onOpenEvent = (payload?: { itemId: number; customerId?: number }): void => {
  if (!payload) return;
  void handleOpen(payload);
};

onMounted(() => {
  eventBus.on('modal.pdv-entregas-notify.open', onOpenEvent);
  eventBus.on('modal.pdv-entregas-notify.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-entregas-notify.open', onOpenEvent);
  eventBus.off('modal.pdv-entregas-notify.close', handleClose);
});
</script>

<style scoped>
.notify-modal-body {
  padding: 8px 0;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--p-text-color, #374151);
  padding: 16px 0;
}

.section-label {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: var(--p-text-color, #374151);
}

.items-section {
  margin-bottom: 20px;
}

.item-row {
  padding: 6px 0;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
}

.item-row:last-child {
  border-bottom: none;
}

.item-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.item-label input[type='checkbox'] {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.item-text {
  color: var(--p-text-color, #374151);
  line-height: 1.4;
}

.empty-state {
  color: var(--p-text-muted-color, #6b7280);
  padding: 12px 0;
}

.message-section {
  margin-top: 8px;
}

.restore-btn {
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 0.8rem;
  background: transparent;
  border: 1px solid var(--p-surface-400, #9ca3af);
  border-radius: 4px;
  cursor: pointer;
  color: var(--p-text-secondary-color, #6b7280);
  transition: background 0.15s;
}

.restore-btn:hover {
  background: var(--p-surface-100, #f3f4f6);
}

.portal-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--p-surface-200, #e5e7eb);
}

.portal-send-btn {
  padding: 6px 14px;
  font-size: 0.85rem;
  background: transparent;
  border: 1px solid var(--p-primary-color, #3b82f6);
  border-radius: 4px;
  color: var(--p-primary-color, #3b82f6);
  cursor: pointer;
  transition: background 0.15s;
}

.portal-send-btn:hover:not(:disabled) {
  background: var(--p-surface-100, #f3f4f6);
}

.portal-send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.portal-results-list {
  list-style: none;
  margin: 12px 0 0 0;
  padding: 0;
}

.portal-results-list li {
  padding: 6px 0;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
  color: var(--p-text-color, #374151);
  font-size: 0.85rem;
}

.portal-results-list li:last-child {
  border-bottom: none;
}

.portal-result-label {
  font-weight: 500;
}

.portal-create-access-btn {
  margin-left: 8px;
  padding: 2px 10px;
  font-size: 0.8rem;
  background: var(--p-primary-color, #3b82f6);
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
}
</style>
